import { useState } from 'react';
import { useUnsavedChanges } from '@shigo-ui/ui';
import { CampaignForm } from './CampaignForm';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  toast,
} from '@shigo-ui/ui';

import { DiscardChangesDialog } from '../../../../../components/forms/DiscardChangesDialog';
import { useBusyDialogGuard } from '../../../../../hooks/useBusyDialogGuard';
import { dashboardT } from '../../../../../lib/messages';
import { useCampaignBrandOptions } from '../../../useCampaignBrandOptions';
import { useCampaignMutations, useCampaignQuery } from '../../hooks';
import { type Campaign } from '../../model';

export function CampaignDialog({
  campaign,
  parentBrand,
  onClose,
}: {
  campaign: Campaign | null;
  parentBrand?: { id: string; name: string } | undefined;
  onClose: () => void;
}) {
  const { hasUnsavedChanges } = useUnsavedChanges();
  const [discard, setDiscard] = useState(false);
  const details = useCampaignQuery(campaign?.id ?? '');
  const brands = useCampaignBrandOptions(!parentBrand);
  const mutations = useCampaignMutations();
  const pending = mutations.create.isPending || mutations.update.isPending;
  const editingCampaign = campaign ? (details.data ?? campaign) : null;
  const loading = Boolean(campaign && details.isLoading);
  const loadError = Boolean(campaign && details.isError);

  const { close: requestClose, onOpenChange } = useBusyDialogGuard(pending, () => {
    if (hasUnsavedChanges) setDiscard(true);
    else onClose();
  });

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] gap-0 overflow-visible p-0">
        <DialogHeader className="border-border border-b px-5 py-3.5">
          <DialogTitle className="text-[15px] font-semibold">
            {campaign ? dashboardT('campaigns.editTitle') : dashboardT('campaigns.createTitle')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {dashboardT('campaigns.formDescription')}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="text-muted-foreground px-5 py-8 text-sm">
            {dashboardT('campaigns.loadingDetails')}
          </p>
        ) : null}
        {loadError ? (
          <div className="space-y-4 px-5 py-6">
            <p role="alert" className="text-destructive text-sm">
              {dashboardT('campaigns.loadDetailsError')}
            </p>
            <Button type="button" variant="outline" onClick={requestClose}>
              {dashboardT('campaigns.cancel')}
            </Button>
          </div>
        ) : null}
        {!loading && !loadError ? (
          <CampaignForm
            key={editingCampaign?.id ?? 'new'}
            campaign={editingCampaign}
            parentBrand={parentBrand}
            brandOptions={parentBrand ? [parentBrand] : (brands.data ?? [])}
            brandsLoading={brands.isLoading}
            pending={pending}
            onCancel={requestClose}
            onSubmit={async (payload) => {
              try {
                if (editingCampaign) {
                  await mutations.update.mutateAsync({ id: editingCampaign.id, payload });
                } else {
                  await mutations.create.mutateAsync(payload);
                }
                toast.success(
                  editingCampaign
                    ? dashboardT('campaigns.updated')
                    : dashboardT('campaigns.createdSuccess'),
                );
                onClose();
              } catch {
                return;
              }
            }}
          />
        ) : null}
        <DiscardChangesDialog
          open={discard}
          onStay={() => {
            setDiscard(false);
          }}
          onDiscard={onClose}
        />
      </DialogContent>
    </Dialog>
  );
}
